import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TemplateDesignElement } from '../../design-element';
import {
  CommunicationTemplate,
  CommunicationTemplateViewControl,
  MassNotificationTemplate,
  MessageSettings
} from 'src/app/modules/communications/store/communications';
import { Store } from '@ngrx/store';
import * as fromAuth from 'src/app/modules/authentication/store/auth/auth.selectors';
import { take } from 'rxjs/operators';
import { UserProfileApiService } from 'src/app/modules/core/store/user-profile';
import produce from 'immer';
import { TextElementSettings } from './text-control-model';
import { TemplateDesignerService } from 'src/app/modules/communications/services/template-designer.service';
import { Dataset } from '@app/modules/core/store/models/dataset.model';
import { Document } from 'src/app/modules/core/store/models/document.model';
import { FieldToValueMapping } from '../../design-elements.models';

@Component({
    selector: 'topo-template-text-element',
    templateUrl: './text-control.component.html',
    styleUrls: ['./text-control.component.scss'],
    standalone: false
})
export class TemplateDesignTextElement implements OnInit, OnDestroy, TemplateDesignElement {
  @Input() index: number;
  @Input() messageSettings?: MessageSettings;
  @Input() template?: CommunicationTemplate;
  @Input() control?: CommunicationTemplateViewControl;
  @Input() set culture(value: string) {
    this._culture = value;
    this.loadData();
  }
  @Input() orgLanguages: string[];
  @Input() selected: boolean;
  @Input() hasEdit?: boolean;
  @Output() settingsChanged = new EventEmitter<{ uId: string; settings: any }>();
  @Output() controlBusy = new EventEmitter();
  @Output() controlReady = new EventEmitter();

  _culture: string;
  editMode: boolean;
  runtimeMode: boolean;
  showRichText: boolean;
  richTextValue: string;
  richTextOrPlaceHolder: string;
  private document: Document;
  private dataset: Dataset;
  private fieldMapping: FieldToValueMapping;
  private organizationUId: string;

  constructor(
    private store: Store<any>,
    private templateDesignerService: TemplateDesignerService,
    private userProfileApiService: UserProfileApiService
  ) {}

  ngOnInit(): void {
    this.store
      .select(fromAuth.selectUserContext)
      .pipe(take(1))
      .subscribe(uc => (this.organizationUId = uc.organizationUId));
  }

  ngOnDestroy(): void {}

  private getEditPlaceHolder(): string {
    if (!!this.templateDesignerService.staticTextTranslations?.['EDIT-TEXT-PLACEHOLDER'][this._culture]) {
      var defaultText = this.templateDesignerService.staticTextTranslations['EDIT-TEXT-PLACEHOLDER'][this._culture];
      return `<div style="font-size: 14px; font-family: Verdana, Geneva, Tahoma, sans-serif; font-weight: 400; color: #000000">${defaultText}</div>`;
    }

    return null;
  }

  loadSettings(): void {
    this.showRichText = false;
    this.editMode = true;
    this.loadData();
  }
  saveSettings(): boolean {
    return true;
  }
  render(): void {
    this.editMode = false;
    this.loadData();
  }

  private loadData(): void {
    const settings: TextElementSettings = this.control?.settings;

    let richTextValue = this.templateDesignerService.getTranslateText(settings?.richText, this._culture);
    if (this.runtimeMode !== true) {
      if (!!richTextValue) {
        this.richTextValue = richTextValue;
        this.richTextOrPlaceHolder = this.richTextValue;
      } else {
        this.richTextOrPlaceHolder = this.getEditPlaceHolder();
      }

      return;
    }

    if (!!richTextValue) {
      if (!!this.document) {
        Object.keys(this.document).forEach(key => {
          const replacement = '{{' + key + '}}';
          if (!!this.fieldMapping && !!this.fieldMapping[key]) {
            richTextValue = richTextValue.replace(replacement, this.fieldMapping[key].value);
          } else {
            richTextValue = richTextValue.replace(replacement, '');
          }
        });
      }
      richTextValue = richTextValue.replace(/{{.+?\}}/g, '');
      this.richTextValue = richTextValue;
      this.richTextOrPlaceHolder = this.richTextValue;
    } else {
      this.richTextOrPlaceHolder = this.getEditPlaceHolder();
    }
  }

  onEditClick(): void {
    this.showRichText = true;
    this.controlBusy.emit();
  }

  updateRichText(text: string): void {
    this.showRichText = false;
    const control = produce(this.control, draftState => {
      if (!!!draftState.settings) {
        draftState.settings = {
          richText: {}
        } as TextElementSettings;
      }
      if (!draftState.settings.richText) {
        draftState.settings.richText = {};
      }
      draftState.settings.richText[this._culture] = text;
    });

    this.control = control;

    this.richTextValue = text;
    this.richTextOrPlaceHolder = this.richTextValue;
    this.settingsChanged.emit({ uId: this.control.uId, settings: control.settings });
  }

  cancelRichText(): void {
    this.showRichText = false;
    this.controlReady.emit();
  }

  getUserList(searchText, renderList) {
    this.userProfileApiService.getUsersForMention(this.organizationUId, searchText).subscribe(res => {
      const entries = res.data.map(p => ({ id: p.uId, value: `${p.displayName}` }));
      renderList(entries);
    });
  }

  edit(): void {
    this.onEditClick();
  }

  setDocumentContext(dataset: Dataset, document: Document, mapUId: string, mapping: FieldToValueMapping) {
    this.dataset = dataset;
    this.document = document;
    this.fieldMapping = mapping;
    this.runtimeMode = true;

    if (!this.editMode) {
      this.loadData();
    }
  }

  setRuntimeData(value: any) {
    this.runtimeMode = true;
    if (!this.editMode && !!value) {
      const settings = value as TextElementSettings;
      let richTextValue = this.templateDesignerService.getTranslateText(settings?.richText, this._culture);
      if (!!richTextValue) {
        this.richTextValue = richTextValue;
        this.richTextOrPlaceHolder = this.richTextValue;
      } else {
        this.richTextOrPlaceHolder = this.getEditPlaceHolder();
      }

      const control = produce(this.control, draftState => {
        if (!!!draftState.settings) {
          draftState.settings = {
            richText: {}
          } as TextElementSettings;
        }
        draftState.settings.richText[this._culture] = this.richTextValue;
      });
    }
  }
}
